import { Component, ElementRef, inject, input, OnChanges, output, signal, SimpleChanges, viewChild, viewChildren } from '@angular/core';
import { delay, unsafe } from '@gametailors/web-core/misc';
import { cn } from '@gametailors/web-core/tailwind';
import { Key, KeyboardColumn, KeyboardRow, SpecialKey } from '../keyboard.types';
import {
  consonantPhonemes,
  fourLetterPhonemes,
  longPhonemes,
  rows,
  shortPhonemes,
  threeLetterPhonemes,
  twoLetterPhonemes,
} from '../keyboard.data';
import { KeyComponent } from '../key.component';
import { KeyboardComponent } from '../keyboard.component';
import { AudioService } from '@gametailors/leerplus';
import { GeneratedAssignment } from '@repo/backend-api-spec';

const SLIDE_ANIMATION_DURATION = 1000 as const;

@Component({
  selector: 'lib-keyboard-activity',
  imports: [KeyComponent, KeyboardComponent],
  templateUrl: './keyboard-activity-base.component.html',
})
export class KeyboardActivityBaseComponent implements OnChanges {
  readonly targetWord = input.required<{ word: string; parts: string[] } | null>();
  readonly assignment = input<GeneratedAssignment>();

  readonly onWrongInput = output<Key>();
  readonly onCorrectInput = output<Key>();
  readonly onCompleteWord = output<void>();

  protected readonly keyboardRows = signal<KeyboardRow[]>([
    {
      columns: [shortPhonemes, longPhonemes],
    },
    {
      columns: [twoLetterPhonemes],
    },
    {
      columns: [threeLetterPhonemes, fourLetterPhonemes],
    },
    {
      columns: [consonantPhonemes],
    },
  ]);

  protected readonly value = signal<Key[]>([]);
  protected readonly wrongKey = signal<Key | undefined>(undefined);
  protected readonly isDone = signal(false);

  protected readonly targetContainer = viewChild<ElementRef<HTMLDivElement>>('answerContainer');
  protected readonly keys = viewChildren<ElementRef<HTMLDivElement>>('key');

  protected readonly audioService = inject(AudioService);
  protected readonly cn: typeof cn = cn;

  async ngOnChanges(changes: SimpleChanges) {
    if ('assignment' in changes && changes['assignment'].currentValue) {
      const newAssignment: GeneratedAssignment = changes['assignment'].currentValue;

      const setDisabled = (col: KeyboardColumn, enabled: boolean) => {
        for (const key of col.keys) {
          if (key instanceof SpecialKey) key.setDisabled(!enabled || newAssignment.letterOrSymbols === 'LETTERS');
          else key.setDisabled(!enabled || newAssignment.letterOrSymbols === 'SYMBOLS');
        }

        return col;
      };

      this.keyboardRows.set([
        {
          columns: [setDisabled(shortPhonemes, newAssignment.shortPhonemes), setDisabled(longPhonemes, newAssignment.longPhonemes)],
        },
        {
          columns: [setDisabled(twoLetterPhonemes, newAssignment.twoLetterPhonemes)],
        },
        {
          columns: [
            setDisabled(threeLetterPhonemes, newAssignment.threeLetterPhonemes),
            setDisabled(fourLetterPhonemes, newAssignment.fourLetterPhonemes),
          ],
        },
        {
          columns: [setDisabled(consonantPhonemes, newAssignment.consonantPhonemes)],
        },
      ]);
    }

    if ('targetWord' in changes) {
      this.targetContainer()
        ?.nativeElement.animate(
          [
            {
              transform: 'translateX(100%)',
              opacity: 0,
            },
            {
              transform: 'translateX(0)',
              opacity: 1,
            },
          ],
          {
            duration: SLIDE_ANIMATION_DURATION,
            easing:
              'linear(0, 0.402 7.4%, 0.711 15.3%, 0.929 23.7%, 1.008 28.2%, 1.067 33%, 1.099 36.9%, 1.12 41%, 1.13 45.4%, 1.13 50.1%, 1.111 58.5%, 1.019 83.2%, 1.004 91.3%, 1)',
            fill: 'forwards',
          },
        )
        .play();

      await delay(SLIDE_ANIMATION_DURATION);
    }
  }

  public async addKey(key: Key) {
    const newKey = key.clone();
    newKey.button.variant = 'outlined';

    this.value.update((input) => [...input, newKey]);

    delay(0).then(() =>
      this.keys()?.at(-1)?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      }),
    );

    await unsafe(async () => {
      await this.audioService.play(`klanken/KosmosKlikker_Klanken_Olivier_${newKey.value}_1.wav`, {
        volume: 0.5,
      }).waitForEnd;
    }).then();
  }

  public removeLastKey() {
    this.value.update((current) => current.slice(0, current.length - 1));
  }

  protected async onKeyPress(key: Key) {
    const target = this.targetWord();

    if (!target) {
      // if there is no target word, we will assume we are in a free-type keyboard.
      await this.addKey(key);
      return;
    }

    const combined = this.value()
      .map((key) => key.value)
      .join(',');

    const complete = combined.length > 0 ? combined + ',' : '';
    const joinedWord = target.parts.join(',') + ',';

    if (key instanceof SpecialKey) {
      const keys = rows[key.value].keys;

      for (const key of keys) {
        if (!joinedWord.startsWith(complete + key.value + ',')) {
          continue;
        }

        await this.addCorrectKey(key);
        return;
      }

      await this.addWrongKey(key);
      return;
    }

    if (!joinedWord.startsWith(complete + key.value + ',')) {
      await this.addWrongKey(key);
      return;
    }

    await this.addCorrectKey(key);
  }

  private async addWrongKey(key: Key) {
    const cloned = key.clone();
    this.wrongKey.set(cloned);
    this.onWrongInput.emit(cloned);

    await Promise.all([
      unsafe(async () => {
        await this.audioService.play(`klanken/KosmosKlikker_Klanken_Olivier_${cloned.value}_1.wav`, {
          volume: 0.5,
        }).waitForEnd;
        await delay(250);
      }),
      delay(1000),
    ]);

    await unsafe(
      () =>
        this.audioService.play(`wrong.mp3`, {
          volume: 0.5,
        }).waitForEnd,
    );

    this.wrongKey.set(undefined);
  }

  private async addCorrectKey(key: Key) {
    const cloned = key.clone();
    cloned.disabled = false;

    this.value.update((input) => [...input, cloned]);
    this.onCorrectInput.emit(cloned);

    unsafe(async () => {
      await this.audioService.play(`klanken/KosmosKlikker_Klanken_Olivier_${cloned.value}_1.wav`, {
        volume: 0.5,
      }).waitForEnd;
    }).then();

    await this.checkWord();
  }

  private async checkWord() {
    const target = this.targetWord();
    if (!target) {
      return;
    }

    const complete = this.value()
      .map((key) => key.value)
      .join(',');

    if (complete != target.parts.join(',')) {
      return;
    }

    this.isDone.set(true);

    await unsafe(
      () =>
        this.audioService.play(`correct.mp3`, {
          volume: 0.5,
        }).waitForEnd,
    );

    await Promise.all([unsafe(() => this.audioService.play(`words/${target.word}.mp3`).waitForEnd), delay(1750)]);

    this.targetContainer()
      ?.nativeElement.animate(
        [
          {
            transform: 'translateX(0)',
            opacity: 1,
          },
          {
            transform: 'translateX(-100%)',
            opacity: 0,
          },
        ],
        {
          duration: SLIDE_ANIMATION_DURATION,
          easing: 'cubic-bezier(0.8, -0.4, 0.5, 1)',
          fill: 'forwards',
        },
      )
      .play();

    await Promise.all([this.audioService.play('swoosh-subtle-soft.mp3').waitForEnd, delay(SLIDE_ANIMATION_DURATION)]);

    this.value.set([]);
    this.isDone.set(false);

    this.onCompleteWord.emit();
  }
}
